import api from './api';
import AppConfig from '../config';

// FreshDesk API configuration
const FRESHDESK_URL = AppConfig.freshdeskUrl || 'https://tourzone.freshdesk.com';
const FRESHDESK_API_KEY = AppConfig.freshdeskApiKey;

// Create FreshDesk API instance
const freshdeskApi = api.create({
  baseURL: FRESHDESK_URL,
  auth: {
    username: FRESHDESK_API_KEY,
    password: 'X' // FreshDesk API key authentication
  }
});

// Mock data for testing
const mockArticles = [
  {
    id: 1,
    title: "How to become a reseller?",
    description: "Step by step guide to become a Tourzone reseller",
    status: 2,
    updated_at: "2024-01-15T10:00:00Z"
  },
  {
    id: 2,
    title: "Reseller commission structure",
    description: "Detailed information about commission rates and structure",
    status: 2,
    updated_at: "2024-01-14T15:30:00Z"
  },
  {
    id: 3,
    title: "Reseller portal access",
    description: "How to access and use the reseller portal",
    status: 2,
    updated_at: "2024-01-13T09:15:00Z"
  },
  {
    id: 4,
    title: "Product catalog for resellers",
    description: "Available products and pricing for resellers",
    status: 2,
    updated_at: "2024-01-12T14:45:00Z"
  },
  {
    id: 5,
    title: "Reseller support contact",
    description: "How to contact reseller support team",
    status: 2,
    updated_at: "2024-01-11T11:20:00Z"
  }
];

// Get all FAQ content (proxy to backend → backend talks to Freshdesk)
export const getAllFaqData = async (language = 'en') => {
  try {
    console.log('🔍 Fetching categories and folders from new API endpoint...');
    const response = await api.get(`/api/faq/categories-with-folders/${language}`);
    console.log('✅ Successfully fetched categories and folders from API');
    return response.data;
  } catch (error) {
    console.error('❌ New API endpoint failed, trying fallback...', error);
    
    // Fallback to old endpoint
    try {
      console.log('🔄 Trying fallback endpoint /api/faq/all...');
      const fallbackResponse = await api.get('/api/faq/all', {
        params: { locale: language }
      });
      console.log('✅ Successfully fetched data from fallback API');
      return fallbackResponse.data;
    } catch (fallbackError) {
      console.error('❌ All API endpoints failed, using mock data...', fallbackError);
      // Return mock data structure as final fallback
      return {
        data: {
          categories: [
            {
              id: 206000027347,
              name: "RESELLER",
              folders: [
                {
                  id: 206000038361,
                  name: "Reseller Information",
                  articles: mockArticles
                }
              ]
            }
          ]
        }
      };
    }
  }
};

// Get categories with folders and articles (new comprehensive endpoint)
export const getCategoriesWithFoldersAndArticles = async (language = 'en') => {
  try {
    console.log(`🔍 Fetching categories with folders and articles for language: ${language}`);
    const response = await api.get(`/api/faq/categories-with-folders-and-articles/${language}`);
    console.log('✅ Successfully fetched categories with folders and articles');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch categories with folders and articles:', error);
    throw error;
  }
};

// Get categories and folders (new optimized endpoint)
export const getCategoriesWithFolders = async (language = 'en') => {
  try {
    console.log(`🔍 Fetching categories and folders for language: ${language}`);
    const response = await api.get(`/api/faq/categories-with-folders/${language}`);
    console.log('✅ Successfully fetched categories and folders');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch categories and folders:', error);
    throw error;
  }
};

// Get articles in a specific folder (new optimized endpoint)
export const getFolderArticles = async (folderId, language = 'en') => {
  try {
    console.log(`🔍 Fetching articles for folder ID: ${folderId} and language: ${language}`);
    const response = await api.get(`/api/faq/folders/${folderId}/articles/${language}`);
    console.log('✅ Successfully fetched folder articles');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch folder articles:', error);
    throw error;
  }
};

// Get article by ID with locale (new optimized endpoint)
export const getArticleByIdWithLocale = async (articleId, language = 'en') => {
  try {
    console.log(`🔍 Fetching article ID: ${articleId} for language: ${language}`);
    const response = await api.get(`/api/faq/articles/${articleId}/locale/${language}`);
    console.log('✅ Successfully fetched article');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch article:', error);
    throw error;
  }
};

// Get articles in a specific folder/category
export const getArticlesInFolder = async (categoryId, language = 'en', folderId = null) => {
  try {
    // Use the real FreshDesk API
    console.log(`🔍 Fetching articles for category ID: ${categoryId}${folderId ? ` and folder ID: ${folderId}` : ''} from real API`);
    
    const response = await api.get('/api/faq/all', {
      params: { locale: language }
    });
    const allData = response.data;
    
    console.log('📦 Raw API response received');
    
    // Find the category in the data.categories array
    const numericCategoryId = parseInt(categoryId, 10);
    const category = allData.data?.categories?.find(cat => cat.id === numericCategoryId);
    
    if (category) {
      console.log(`📋 Found category: ${category.name}`, category);
      
      if (folderId) {
        // Eğer folder ID varsa, sadece o klasörün makalelerini getir
        const numericFolderId = parseInt(folderId, 10);
        const folder = category.folders?.find(f => f.id === numericFolderId);
        
        if (folder) {
          console.log(`📁 Found folder: ${folder.name} with ${folder.articles?.length || 0} articles`);
          console.log(`📁 Folder details:`, folder);
          const folderArticles = folder.articles || [];
          console.log(`📄 Found ${folderArticles.length} articles for folder ${folderId}`);
          console.log(`📄 Articles:`, folderArticles);
          return { data: folderArticles };
        } else {
          console.log(`❌ Folder ${folderId} not found in category ${categoryId}`);
          return { data: [] };
        }
      } else {
        // Eğer folder ID yoksa, tüm kategori makalelerini getir
        let allArticles = [];
        if (category.folders && Array.isArray(category.folders)) {
          console.log(`📁 Found ${category.folders.length} folders in category`);
          category.folders.forEach((folder, index) => {
            console.log(`📁 Folder ${index + 1}: ${folder.name} with ${folder.articles?.length || 0} articles`);
            if (folder.articles && Array.isArray(folder.articles)) {
              allArticles = allArticles.concat(folder.articles);
            }
          });
        }
        
        console.log(`📄 Found ${allArticles.length} articles for category ${categoryId}`);
        return { data: allArticles };
      }
    }
    
    console.log('❌ Category not found in API response');
    return { data: [] };
    
  } catch (error) {
    console.error(`❌ Error fetching articles for category ${categoryId}:`, error);
    
    // Fallback to mock data for testing
    console.log('🔄 Falling back to mock data...');
    const numericCategoryId = parseInt(categoryId, 10);
    
    if (numericCategoryId === 206000027347) {
      return { data: mockArticles };
    }
    
    return { data: [] };
  }
};

// Get FAQ categories
export const getSolutionCategories = async () => {
  try {
    const response = await api.get('/api/faq/categories');
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return [
      {
        id: 206000027347,
        name: "RESELLER",
        description: "Reseller information and guides"
      }
    ];
  }
};

// Get all articles
export const getAllArticles = async () => {
  try {
    const response = await api.get('/api/faq/articles');
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return mockArticles;
  }
};

// Get a specific article
export const getArticleById = async (articleId, language = 'en') => {
  try {
    const response = await api.get(`/api/faq/articles/${articleId}`, {
      params: { locale: language }
    });
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    const article = mockArticles.find(a => a.id === parseInt(articleId, 10));
    return article || null;
  }
};

// Enhanced search with sorting options and category filtering
export const enhancedSearch = async (query, sortBy = 'relevance', categoryId = null) => {
  try {
    const params = { query, sortBy };
    if (categoryId && categoryId !== 'all') {
      params.categoryId = categoryId;
    }
    
    const response = await api.get('/api/faq/search/enhanced', { params });
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    // Simple mock search
    const filteredArticles = mockArticles.filter(article => 
      article.title.toLowerCase().includes(query.toLowerCase()) ||
      article.description.toLowerCase().includes(query.toLowerCase())
    );
    return { data: filteredArticles };
  }
};

// Get FAQ analytics
export const getFaqAnalytics = async () => {
  try {
    const response = await api.get('/api/faq/analytics');
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return { totalArticles: mockArticles.length, totalViews: 1000 };
  }
};

// Get related articles
export const getRelatedArticles = async (articleId, limit = 5) => {
  try {
    const response = await api.get(`/api/faq/articles/${articleId}/related`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return mockArticles.slice(0, limit);
  }
};

// Get all tags
export const getAllTags = async () => {
  try {
    const response = await api.get('/api/faq/tags');
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return ['reseller', 'commission', 'portal', 'support'];
  }
};

// Get articles by specific tags
export const getArticlesByTags = async (tags) => {
  try {
    const response = await api.get(`/api/faq/tags/${tags}`);
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return mockArticles;
  }
};

// Get optimized content
export const getOptimizedContent = async () => {
  try {
    const response = await api.get('/api/faq/optimized');
    return response.data;
  } catch (error) {
    console.error('Backend API failed, using mock data...');
    return mockArticles;
  }
};

// Create FreshDesk ticket
export const createFreshDeskTicket = async (ticketData) => {
  try {
    console.log('📝 Creating FreshDesk ticket:', ticketData);
    
    // Use the new help-center ticket API
    const response = await api.post('/api/help-center/ticket', ticketData);
    
    console.log('✅ Ticket created successfully:', response.data);
    return {
      success: true,
      ticket_id: response.data.id || response.data.ticket_id,
      message: 'Ticket created successfully',
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error creating ticket:', error);
    
    // Fallback: Mock response for testing
    console.log('🔄 Using mock response for ticket creation');
    return {
      success: true,
      ticket_id: Math.floor(Math.random() * 10000) + 1000,
      message: 'Ticket created successfully (mock)',
      data: {
        id: Math.floor(Math.random() * 10000) + 1000,
        subject: ticketData.subject,
        status: 'open',
        priority: ticketData.priority || 'medium',
        created_at: new Date().toISOString()
      }
    };
  }
};

// Get FreshDesk ticket status
export const getTicketStatus = async (ticketId) => {
  try {
    const response = await api.get(`/api/tickets/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching ticket status:', error);
    return null;
  }
};

// Upload file to FreshDesk
export const uploadFileToFreshDesk = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/api/attachments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('❌ Error uploading file:', error);
    return null;
  }
};

// Submit article helpful feedback
export const submitArticleFeedback = async (articleId, isHelpful, language = 'en') => {
  try {
    console.log(`📝 Submitting feedback for article ${articleId}: ${isHelpful ? 'helpful' : 'not helpful'}`);
    
    const response = await api.post(`/api/faq/articles/${articleId}/feedback`, {
      helpful: isHelpful,
      locale: language
    });
    
    console.log('✅ Feedback submitted successfully');
    return response.data;
  } catch (error) {
    console.error('❌ Error submitting feedback:', error);
    
    // Fallback: Local storage'a kaydet (gerçek API olmadığında)
    const feedbackKey = `article_feedback_${articleId}`;
    const existingFeedback = localStorage.getItem(feedbackKey);
    const feedbackData = existingFeedback ? JSON.parse(existingFeedback) : { helpful: 0, notHelpful: 0 };
    
    if (isHelpful) {
      feedbackData.helpful += 1;
    } else {
      feedbackData.notHelpful += 1;
    }
    
    localStorage.setItem(feedbackKey, JSON.stringify(feedbackData));
    
    // Mock response
    return {
      success: true,
      message: 'Feedback recorded locally',
      data: feedbackData
    };
  }
};

// Get article feedback statistics
export const getArticleFeedback = async (articleId) => {
  try {
    const response = await api.get(`/api/faq/articles/${articleId}/feedback`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching feedback:', error);
    
    // Fallback: Local storage'dan oku
    const feedbackKey = `article_feedback_${articleId}`;
    const existingFeedback = localStorage.getItem(feedbackKey);
    
    if (existingFeedback) {
      return JSON.parse(existingFeedback);
    }
    
    // Default values
    return {
      helpful: 0,
      notHelpful: 0,
      total: 0
    };
  }
};

// Get available locales/languages from API
export const getAvailableLocales = async () => {
  try {
    console.log('🌍 Fetching available locales from API...');
    const response = await api.get('/api/faq/locales');
    console.log('✅ Successfully fetched locales from API');
    return response.data;
  } catch (error) {
    console.error('❌ Locales API failed, using fallback locales...', error);
    // Fallback to default locales
    return {
      locales: [
        {
          code: 'en',
          name: 'English',
          nativeName: 'English',
          flag: '🇬🇧',
          isDefault: true
        },
        {
          code: 'tr',
          name: 'Turkish',
          nativeName: 'Türkçe',
          flag: '🇹🇷',
          isDefault: false
        },
        {
          code: 'ru',
          name: 'Russian',
          nativeName: 'Русский',
          flag: '🇷🇺',
          isDefault: false
        }
      ]
    };
  }
};

// Get available locales/languages
export const getLocales = async () => {
  try {
    console.log('🌍 Fetching available locales from API...');
    const response = await api.get('/api/faq/locales');
    console.log('✅ Successfully fetched locales:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch locales, using fallback...', error);
    // Fallback to default locales
    return {
      success: true,
      data: [
        { code: 'en', name: 'English', hasContent: true, categoryCount: 2 },
        { code: 'tr', name: 'Türkçe', hasContent: true, categoryCount: 1 }
      ],
      count: 2
    };
  }
};
